import { FC, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { PaymentCardType } from '@shinetools/pricing-plan-library';
import { Color } from '@shinetools/suntheme';

import Text from 'components/_core/Text';
import { TopBar } from 'features/Onboarding/modules/StepsPricingPlan/components/StepsLayout';
import { PlanState } from 'types/planUtils';

import basicCard from '../../../../assets/rebranded/basicCard.png';
import doubleCard from '../../../../assets/rebranded/doubleCard.png';
import businessWorldCard from '../../../../assets/rebranded/plusCard.png';
import tripleCard from '../../../../assets/rebranded/tripleCard.png';
import { OnboardingPlanDisplay } from '../../../../types';
import PlanCard from './components/PlanCard';
import locales from './locales';

interface CardConfiguration {
  color: Color;
  icon: string;
}

interface PlanCardsProps {
  onClose: () => void;
  onboardingPlans: OnboardingPlanDisplay[];
  currentPlan?: PlanState;
  isLoading: boolean;
  referrer?: string;
  highlightHeight: number;
  onChoose: (plan: OnboardingPlanDisplay) => void;
  showDetails: boolean;
}

const getCardConfiguration = (
  plan: OnboardingPlanDisplay,
): CardConfiguration => {
  const color = plan.brandColor as Color;
  let icon = basicCard;

  if (plan.limits.physicalPaymentCard.value === 2) {
    icon = doubleCard;
  } else if (plan.limits.physicalPaymentCard.value > 2) {
    icon = tripleCard;
  } else if (plan.paymentCard.type === PaymentCardType.BusinessWorldDebitCard) {
    icon = businessWorldCard;
  }

  return {
    color,
    icon,
  };
};

const renderPlanCard = ({
  currentPlan,
  highlightHeight,
  index,
  isLoading,
  onChoose,
  plan,
  setFirstItemVisible,
  showDetails,
}: {
  plan: OnboardingPlanDisplay;
  index: number;
  isLoading: boolean;
  currentPlan?: PlanState;
  highlightHeight: number;
  onChoose: (plan: OnboardingPlanDisplay) => void;
  setFirstItemVisible: (visibility: boolean) => void;
  showDetails: boolean;
}) => {
  const isFirst = index === 0;
  const cardConfiguration = getCardConfiguration(plan);

  return (
    <PlanCard
      currentPlan={currentPlan}
      highlightHeight={highlightHeight}
      icon={cardConfiguration.icon}
      isButtonLoading={isLoading}
      isFirst={isFirst}
      key={plan.brandName}
      onChoose={onChoose}
      onVisibilityChange={isFirst ? setFirstItemVisible : undefined}
      plan={plan}
      showDetails={showDetails}
    />
  );
};

const PlanCards: FC<PlanCardsProps> = ({
  currentPlan,
  highlightHeight,
  isLoading,
  onboardingPlans,
  onChoose,
  referrer,
  showDetails,
}) => {
  const [shouldShowBar, setShouldShowBar] = useState(false);

  const setFirstItemVisible = (visibility: boolean) => {
    setShouldShowBar(!visibility);
  };

  const nbPlans = onboardingPlans.length;

  return (
    <>
      <TopBar.Content>
        {referrer && !shouldShowBar ? (
          <Flex
            alignItems="center"
            background="blue.300"
            borderRadius="radius-8"
            height="2rem"
            paddingX="space-16"
            width="100%"
          >
            <Box marginRight="space-4">🎉</Box>
            <Text
              fontWeight="weight-500"
              size="sm"
              sx={{
                strong: {
                  color: 'blue.-1',
                  fontWeight: 'weight-500',
                },
              }}
            >
              {locales.formatString(locales.refferalOffer, referrer)}
            </Text>
          </Flex>
        ) : null}
      </TopBar.Content>
      <TopBar.Plans isExpanded={shouldShowBar} nbPlans={nbPlans} />
      {onboardingPlans.map((plan, index) =>
        renderPlanCard({
          currentPlan,
          highlightHeight,
          index,
          isLoading,
          onChoose,
          plan,
          setFirstItemVisible,
          showDetails,
        }),
      )}
    </>
  );
};

export default PlanCards;
